import {logger} from '@zoefin/logger';

import {
  AUTH0_ACCESS_TOKEN_ENDPOINT,
  SECONDS_TO_MILLISECONDS,
  USER_TOKEN_TIMEOUT_MS,
} from '../../constants/request';
import {classifyRequestFailure} from '../../errors/utils';
import {isBrowserRuntime} from '../../runtime/browser';
import {createRequestTimeoutSignal} from '../../runtime/signal';
import {
  flushSpansOnError,
  injectTracingHeaders,
  setResponseAttributes,
  setSpanError,
  startRequestSpan,
  withRequestSpanContext,
} from '../../tracing';
import type {UserAccessToken, UserAccessTokenPayload} from '../../types/auth';
import {RequestAuthTokenSource, ResolvedRequest} from '../../types/route';
import {sanitizeUrlForLog} from '../../utils/redaction';
import {resolveInternalOrigin} from '../../utils/url';

import {AUTHTOKEN_RESOLVED_REQUEST} from './constants';
import type {UserAuthorizationResult, UserTokenCacheGlobal} from './types';

const getCachedUserToken = (): UserAccessToken | undefined => {
  if (!isBrowserRuntime()) {
    return undefined;
  }

  const cache = (globalThis as UserTokenCacheGlobal).__zoeRequestUserTokenCache;

  if (!cache?.accessToken) {
    return undefined;
  }

  if (!cache.expiresAt) {
    return cache;
  }

  if (Date.now() < cache.expiresAt) {
    return cache;
  }

  return undefined;
};

const setCachedUserToken = (token: UserAccessToken | null): void => {
  if (!isBrowserRuntime()) {
    return;
  }

  (globalThis as UserTokenCacheGlobal).__zoeRequestUserTokenCache = token;
};

const parseStringExpiry = (expiresAt: string): number | undefined => {
  const numericValue = Number(expiresAt);

  if (!Number.isNaN(numericValue)) {
    return numericValue;
  }

  const parsedValue = Date.parse(expiresAt);

  return Number.isNaN(parsedValue) ? undefined : parsedValue;
};

const parseExpiry = (payload: UserAccessTokenPayload): number | undefined => {
  const {expiresAt, expires_in: expiresIn} = payload;

  if (typeof expiresAt === 'number') {
    return expiresAt;
  }

  if (typeof expiresAt === 'string') {
    return parseStringExpiry(expiresAt);
  }

  if (typeof expiresIn !== 'number') {
    return undefined;
  }

  return Date.now() + expiresIn * SECONDS_TO_MILLISECONDS;
};

const parseUserAccessToken = (
  payload: UserAccessTokenPayload,
): UserAccessToken | undefined => {
  const accessToken = payload.token ?? payload.accessToken;

  if (!accessToken) {
    return undefined;
  }

  return {
    accessToken,
    expiresAt: parseExpiry(payload),
  };
};

/**
 * When running in a Next.js server context, reads the middleware-injected
 * `x-zoe-access-token` header and the request cookies from `next/headers`.
 *
 * Loads `next/headers` lazily so non-Next.js runtimes never evaluate it at
 * module load. Falls back to an empty object when not in a Next.js request
 * context (non-Next.js runtimes, test environments, browser).
 */
const resolveFromNextHeaders = async (): Promise<{
  middlewareToken?: string;
  cookieHeader?: string;
}> => {
  if (isBrowserRuntime()) {
    return {};
  }

  try {
    const {headers} = await import('next/headers');
    const requestHeaders = await headers();
    const middlewareToken =
      requestHeaders.get('x-zoe-access-token') ?? undefined;
    const cookieHeader = requestHeaders.get('cookie') ?? undefined;

    return {middlewareToken, cookieHeader};
  } catch {
    logger.warn('Error resolving user authorization from next headers');

    return {};
  }
};

/**
 * In-flight token request promise used to deduplicate parallel calls on the
 * browser. Module-scoped so it's per-tab (safe). Only used in browser runtime —
 * server requests must not share promises across user contexts.
 */
let inflightTokenRequest: Promise<UserAuthorizationResult> | null = null;

/**
 * Fetches a user access token from the `/api/authtoken` endpoint.
 *
 * Handles tracing, error logging, and populating the in-memory cache on success.
 */
const fetchUserToken = async ({
  requestOrigin,
  cookieHeader,
}: {
  requestOrigin: string;
  cookieHeader?: string;
}): Promise<UserAuthorizationResult> => {
  const endpoint = new URL(
    AUTH0_ACCESS_TOKEN_ENDPOINT,
    resolveInternalOrigin(requestOrigin),
  );
  const {signal} = createRequestTimeoutSignal({
    timeoutMs: USER_TOKEN_TIMEOUT_MS,
  });
  const isBrowser = isBrowserRuntime();
  const requestHeaders = new Headers();

  if (cookieHeader) {
    requestHeaders.set('cookie', cookieHeader);
  }

  const authtokenResolvedRequest: ResolvedRequest = {
    ...AUTHTOKEN_RESOLVED_REQUEST,
    url: endpoint.toString(),
  };
  const span = startRequestSpan({
    body: undefined,
    headers: requestHeaders,
    method: 'GET',
    requestUrl: endpoint,
    resolvedRequest: authtokenResolvedRequest,
  });

  return withRequestSpanContext(span, async () => {
    injectTracingHeaders({
      headers: requestHeaders,
      requestUrl: endpoint,
      resolvedRequest: authtokenResolvedRequest,
    });

    try {
      const response = await fetch(endpoint, {
        method: 'GET',
        cache: 'no-store',
        headers: requestHeaders,
        ...(isBrowser ? {credentials: 'include' as const} : {}),
        signal,
      });
      const payload = response.ok
        ? ((await response.json()) as UserAccessTokenPayload)
        : undefined;

      setResponseAttributes({
        parsedData: payload
          ? {
              expiresAt: payload.expiresAt,
              expires_in: payload.expires_in,
              hasToken: Boolean(payload.accessToken ?? payload.token),
            }
          : undefined,
        response,
        span,
      });

      if (!response.ok) {
        const message = `Failed to resolve Auth0 user access token (${response.status} ${response.statusText})`;

        setSpanError({
          error: new Error(message),
          span,
          status: response.status,
        });
        logger.warn(
          {
            status: response.status,
            statusText: response.statusText,
            endpoint: sanitizeUrlForLog(endpoint),
          },
          'Failed to resolve Auth0 user access token',
        );

        return {
          tokenSource: RequestAuthTokenSource.NONE,
        };
      }

      if (!payload) {
        logger.warn(
          {
            endpoint: sanitizeUrlForLog(endpoint),
          },
          'Auth0 access token response body was empty',
        );

        return {
          tokenSource: RequestAuthTokenSource.NONE,
        };
      }

      const parsedToken = parseUserAccessToken(payload);

      if (!parsedToken?.accessToken) {
        logger.warn(
          {
            endpoint: sanitizeUrlForLog(endpoint),
          },
          'Auth0 access token response did not contain a token',
        );

        return {
          tokenSource: RequestAuthTokenSource.NONE,
        };
      }

      setCachedUserToken(parsedToken);

      return {
        token: parsedToken.accessToken,
        tokenSource: RequestAuthTokenSource.AUTHTOKEN_ENDPOINT,
      };
    } catch (error) {
      setSpanError({
        error,
        span,
      });

      logger.warn(
        {
          error,
          endpoint: sanitizeUrlForLog(endpoint),
          isBrowser,
          failureReason: classifyRequestFailure(error),
        },
        'Auth0 token bootstrap request failed',
      );
      flushSpansOnError();

      return {
        tokenSource: RequestAuthTokenSource.NONE,
      };
    } finally {
      span.end();
    }
  });
};

/**
 * Resolves a user bearer token for the outgoing request.
 *
 * Lookup order:
 * 1. In-memory cache (browser only, with expiry check).
 * 2. `x-zoe-access-token` header injected by Next.js middleware (server only).
 * 3. Auth0 `/api/authtoken` endpoint — browser uses credentials, server forwards cookies.
 *
 * On the browser, parallel calls are deduplicated so only one `/api/authtoken`
 * fetch is in-flight at a time. All concurrent callers share the same promise.
 *
 * Returns `tokenSource: 'none'` when no token can be obtained (caller falls back to passthrough).
 */
export const resolveUserAuthorization = async ({
  requestOrigin,
  headers,
}: {
  requestOrigin: string;
  headers: Headers;
}): Promise<UserAuthorizationResult> => {
  const cachedUserToken = getCachedUserToken();

  if (cachedUserToken?.accessToken) {
    return {
      token: cachedUserToken.accessToken,
      tokenSource: RequestAuthTokenSource.BROWSER_MEMORY_CACHE,
    };
  }

  // On the server, the middleware injects x-zoe-access-token into every
  // authenticated request. Use it directly to avoid an extra /api/authtoken
  // round-trip that would fail with missing_session because services don't
  // forward session cookies when calling request().
  const {middlewareToken, cookieHeader: nextCookieHeader} =
    await resolveFromNextHeaders();

  if (middlewareToken) {
    return {
      token: middlewareToken,
      tokenSource: RequestAuthTokenSource.AUTHTOKEN_ENDPOINT,
    };
  }

  const cookieHeader = isBrowserRuntime()
    ? undefined
    : headers.get('cookie') || nextCookieHeader || undefined;

  // Deduplicate parallel browser calls — only one /api/authtoken fetch at a time
  if (isBrowserRuntime() && inflightTokenRequest) {
    return inflightTokenRequest;
  }

  const tokenPromise: Promise<UserAuthorizationResult> = fetchUserToken({
    requestOrigin,
    cookieHeader,
  });

  if (isBrowserRuntime()) {
    inflightTokenRequest = tokenPromise;
  }

  try {
    return await tokenPromise;
  } catch (error) {
    logger.error(
      {
        error,
      },
      'Failed to resolve user authorization',
    );

    return {
      tokenSource: RequestAuthTokenSource.NONE,
    };
  } finally {
    inflightTokenRequest = null;
  }
};
